(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.ReCaptchaToken = global.NewHomeSource.ReCaptchaToken || {};

  class ReCaptchaToken {
    /**
     * Initializes a new instance of the class.
     * @constructor
     * @property {number|null} widgetId - The ID of the reCAPTCHA widget.
     * @property {Function|null} resolveValidation - Callback to resolve validation.
     * @property {number} reValidationsAttempts - Number of allowed re-validation attempts.
     * @property {HTMLIFrameElement|null} iframeChallenge - Reference to the iframe challenge element.
     * @property {string} validateRequest - URL for reCAPTCHA token validation.
     * @property {HTMLElement|null} container - Container element for the widget.
     */
    constructor() {
      this.widgetId = null;
      this.resolveValidation = null;
      this.reValidationsAttempts = 4;
      this.iframeChallenge = null;
      this.validateRequest = '/ajax/formvalidation/validaterecaptchatoken';
      this.placeHolderHtml = '<div data-recaptcha-placeholder id="recaptcha-placeholder" style="display:none;"></div>';
      this.container = null;
    }

    /**
     * Initializes the reCAPTCHA widget with the provided container element.
     * Sets up an invisible reCAPTCHA widget using the site key from the container's data attribute.
     *
     * @param {HTMLElement} container - The DOM element containing the reCAPTCHA configuration
     * @returns {Object} Returns this instance for method chaining
     */
    initialize(container) {
      this.container = container;
      if (!global.grecaptcha || !container) {
        return this;
      }

      const siteKey = container.querySelector('[data-re-captcha-site-key]');
      if (!siteKey) {
        return this;
      }

      this.createPlaceHolder();
      this.widgetId = global.grecaptcha.render(this.getPlaceHolder(), {
        sitekey: siteKey.value,
        callback: this.onSuccess.bind(this),
        size: 'invisible'
      });

      this.startChallengeObserver();

      return this;
    }

    /**
     * Creates and inserts a placeholder element after the input element.
     * Uses the placeHolderHtml property to create a document fragment and
     * inserts the first child of that fragment immediately after the input.
     */
    createPlaceHolder() {
      const input = this.getInput();
      const placeHolderExists = this.getPlaceHolder();
      if (!input || placeHolderExists) {
        return;
      }
      const fragment = document.createRange().createContextualFragment(this.placeHolderHtml);
      input.insertAdjacentElement('afterend', fragment.firstChild);
    }

    /**
     * Gets the reCAPTCHA placeholder element from the container.
     * @returns {Element|null} The element with the data-recaptcha-placeholder attribute, or null if not found.
     */
    getPlaceHolder() {
      if (!this.container) {
        return null;
      }
      return this.container.querySelector('[data-recaptcha-placeholder]');
    }

    /**
     * Retrieves the input element with the reCAPTCHA token data attribute.
     * @returns {HTMLElement|null} The input element containing the reCAPTCHA token, or null if not found.
     */
    getInput() {
      if (!this.container) {
        return null;
      }
      return this.container.querySelector('[data-re-captcha-token]');
    }

    /**
     * Sets the value of the input element associated with this reCaptcha token instance.
     * @param {string} value - The value to set in the input field
     * @returns {void}
     */
    setValue(value) {
      const input = this.getInput();
      if (input) {
        input.value = value;
      }
    }

    /**
     * Gets the current value of the reCAPTCHA token input element.
     * @returns {string|undefined} The value of the input element, or undefined if the input doesn't exist
     */
    getValue() {
      const input = this.getInput();
      return input && input.value;
    }

    /**
     * Disconnects and cleans up all active observers.
     * This method safely disconnects the main observer and challenge observer if they exist,
     * and sets their references to null to prevent memory leaks.
     */
    disconnectObservers() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.challengeObserver) {
        this.challengeObserver.disconnect();
        this.challengeObserver = null;
      }
    }

    /**
     * Sets the resolved attribute on the input element to indicate reCAPTCHA resolution status.
     * @param {boolean} isResolved - Whether the reCAPTCHA has been successfully resolved
     */
    setResolveAttribute(isResolved) {
      const input = this.getInput();
      if (input) {
        input.dataset.resolved = isResolved;
      }
    }

    /**
     * Handles successful reCAPTCHA token resolution by setting the token value and resolving validation.
     * @param {string} token - The reCAPTCHA token received from successful validation
     * @returns {void}
     */
    resolveTokenSuccessfully(token) {
      this.setResolveAttribute(true);
      this.resolveToken(token);
    }

    /**
     * Resolves the reCAPTCHA token by setting its value and completing validation.
     * This method is typically called when a reCAPTCHA token is successfully obtained.
     *
     * @param {string} token - The reCAPTCHA token received from Google's reCAPTCHA service
     * @returns {void}
     */
    resolveToken(token) {
      this.setValue(token);
      this.resolveValidation(true);
      this.resolveValidation = null;
    }

    /**
     * Resolves a reCAPTCHA token without performing validation.
     * Sets the resolve attribute to false and then processes the token.
     * @param {string} token - The reCAPTCHA token to resolve
     */
    resolveTokenWithoutValidation(token) {
      this.setResolveAttribute(false);
      this.resolveToken(token);
    }

    /**
     * Validates a reCAPTCHA token by sending it to the server for verification.
     * @param {string} token - The reCAPTCHA token to validate
     * @returns {Promise<Response>} A Promise that resolves to the fetch Response
     * object containing the validation result
     */
    validateReCaptchaToken(token) {
      return fetch(this.validateRequest, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ ReCaptchaToken: token }),
      });
    }

    /**
     * Handles the successful generation of a reCAPTCHA token.
     * Validates the token with the server and either resolves successfully or retries
     * based on validation results and available retry attempts.
     *
     * @param {string} token - The reCAPTCHA token generated upon successful completion
     */
    onSuccess(token) {
      this.token = token;
      this.validateReCaptchaToken(token)
        .then((response) => response.json())
        .then((result) => {
          if (result.Success) {
            this.resolveTokenSuccessfully(token);
          } else if (this.reValidationsAttempts > 1) {
            this.reValidationsAttempts -= 1;
            this.token = null;
            global.grecaptcha.reset(this.widgetId);
            global.grecaptcha.execute(this.widgetId);
          } else if (this.resolveValidation) {
            this.resolveTokenWithoutValidation(token);
          }
        })
        .catch(() => {
          if (this.reValidationsAttempts > 1) {
            this.reValidationsAttempts -= 1;
            this.token = null;
            global.grecaptcha.reset(this.widgetId);
            global.grecaptcha.execute(this.widgetId);
          } else if (this.resolveValidation) {
            this.resolveTokenWithoutValidation(token);
          }
        });
    }

    /**
     * Starts observing DOM mutations to detect reCAPTCHA challenge iframe elements and monitor their visibility states.
     *
     * This method sets up a MutationObserver to watch for the addition of reCAPTCHA iframe elements to the DOM.
     * When a challenge iframe is detected, it creates an additional observer to monitor style changes that indicate
     * when the challenge becomes hidden (indicating completion or dismissal).
     *
     * Note: This implementation is necessary because reCAPTCHA doesn't provide a native API to notify when
     * the challenge UI is ready or when it becomes hidden, requiring us to monitor DOM changes manually.
     */
    startChallengeObserver() {
      if (!global.grecaptcha || !this.getInput()) {
        return;
      }

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.tagName === 'IFRAME' && node.src && node.src.includes('www.google.com/recaptcha/api2/bframe')) {
              this.iframeChallenge = node;

              const challengeContainer = node.parentElement && node.parentElement.parentElement;
              if (challengeContainer) {
                this.challengeObserver = new MutationObserver(() => {
                  const isHidden =
                  node.style.height !== '100%' &&
                  challengeContainer.style.opacity === '0';

                  if (isHidden && this.resolveValidation && !this.token) {
                    this.resolveValidation(false);
                    this.resolveValidation = null;
                  }
                });

                this.challengeObserver.observe(challengeContainer, {
                  attributes: true,
                  attributeFilter: ['style']
                });
              }
            }
          });
        });
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    /**
     * Unbinds all events and cleans up resources associated with the reCAPTCHA token component.
     * This method disconnects observers, resolves any pending validation with a false result,
     * and removes the iframe challenge element from the DOM if it exists.
     */
    unbindEvents() {
      const placeHolder = this.getPlaceHolder();
      this.disconnectObservers();

      if (this.resolveValidation) {
        this.resolveValidation(false);
        this.resolveValidation = null;
      }

      if (this.iframeChallenge) {
        this.iframeChallenge.remove();
      }

      if (placeHolder) {
        placeHolder.remove();
      }
    }

    /**
     * Validates the reCAPTCHA input field and executes reCAPTCHA challenge if required.
     *
     * @returns {Promise<boolean>} A promise that resolves to true if validation passes,
     *                            or resolves when reCAPTCHA challenge is completed successfully.
     *                            Always resolves to true if input is not required, widget ID is invalid,
     *                            or if reCAPTCHA value already exists.
     */
    validate() {
      const input = this.getInput();
      if (!input || !global.grecaptcha) {
        return Promise.resolve(true);
      }

      const isRequired = input.hasAttribute('required');
      const isWidgetIdValid = this.widgetId !== null &&
      this.widgetId !== undefined &&
      typeof this.widgetId === 'number';

      if (!isRequired || !isWidgetIdValid || this.getValue()) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        this.resolveValidation = resolve;
        global.grecaptcha.execute(this.widgetId);
      });
    }
  }

  global.NewHomeSource.ReCaptchaToken = ReCaptchaToken;
}(window));
